import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  TouchableOpacity,
  Image,
  Dimensions,
  FlatList,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { MARKETPLACE_COLORS } from '@/constants/residents/marketplaceColors';
import { CarouselDots } from './CarouselDots';
import { COMMUNITY_RESOURCE_ICONS } from '@/assets/admin/CommunityResourceIcons';
import { RESIDENTS_SVG_COMPONENTS } from '@/assets/residents';
import { SVG_COMPONENTS } from '@/assets/admin';

const BookmarkIcon = COMMUNITY_RESOURCE_ICONS.bookmarkIcon;
const BookmarkFilledIcon = COMMUNITY_RESOURCE_ICONS.bookmarkFilledIcon;
const PropertyPlaceholder = RESIDENTS_SVG_COMPONENTS.PROPERTY_PLACEHOLDER;
const ChevronLeftIcon = SVG_COMPONENTS.CHEVRON_LEFT;
const ChevronRightIcon = SVG_COMPONENTS.CHEVRON_RIGHT;

const { width: SCREEN_WIDTH } = Dimensions.get('window');

type LayoutMode = 'mobile' | 'tablet' | 'desktop';

interface ImageCarouselProps {
  images: string[];
  onImagePress?: (index: number) => void;
  // Bookmark props (optional - for buyer view)
  showBookmark?: boolean;
  isBookmarked?: boolean;
  onBookmarkPress?: () => void;
  // Custom dimensions
  width?: number;
  height?: number;
  // Carousel dots position
  dotsPosition?: 'bottom-left' | 'bottom-center';
  // Optional layout mode for responsive styling
  layoutMode?: LayoutMode;
  // Show navigation arrows (for desktop/tablet)
  showArrows?: boolean;
}

export const ImageCarousel: React.FC<ImageCarouselProps> = ({
  images,
  onImagePress,
  showBookmark = false,
  isBookmarked = false,
  onBookmarkPress,
  width,
  height,
  dotsPosition = 'bottom-center',
  layoutMode = 'mobile',
  showArrows = false,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const flatListRef = useRef<FlatList>(null);
  
  const isLargeScreen = layoutMode !== 'mobile';

  // Default dimensions based on screen width with padding
  const imageWidth = width ?? SCREEN_WIDTH - 40;
  const imageHeight = height ?? imageWidth * 0.9;
  
  // Responsive sizes for bookmark button
  const bookmarkSize = isLargeScreen ? 40 : 34;
  const bookmarkIconSize = isLargeScreen ? 24 : 20;

  const hasImages = images && images.length > 0;
  const canGoNext = activeIndex < images.length - 1;
  const canGoPrev = activeIndex > 0;
  const shouldShowArrows = showArrows && isLargeScreen && images.length > 1;

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const slideSize = event.nativeEvent.layoutMeasurement.width;
    const index = event.nativeEvent.contentOffset.x / slideSize;
    setActiveIndex(Math.round(index));
  };

  const goToNext = useCallback(() => {
    if (canGoNext) {
      const newIndex = activeIndex + 1;
      setActiveIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [activeIndex, canGoNext]);

  const goToPrev = useCallback(() => {
    if (canGoPrev) {
      const newIndex = activeIndex - 1;
      setActiveIndex(newIndex);
      flatListRef.current?.scrollToIndex({ index: newIndex, animated: true });
    }
  }, [activeIndex, canGoPrev]);

  const renderItem = ({ item, index }: { item: string; index: number }) => (
    <TouchableOpacity
      style={[styles.imageSlide, { width: imageWidth, height: imageHeight }]}
      onPress={() => onImagePress?.(index)}
      activeOpacity={0.9}
      disabled={!onImagePress}
    >
      <Image
        source={{ uri: item }}
        style={[styles.carouselImage, { width: imageWidth, height: imageHeight }]}
        resizeMode="cover"
      />
    </TouchableOpacity>
  );

  // Show placeholder when no images
  if (!hasImages) {
    return (
      <View style={[styles.container, styles.placeholderContainer, { width: imageWidth, height: imageHeight }]}>
        <PropertyPlaceholder width={imageWidth * 0.6} height={imageHeight * 0.6} />
        
        {/* Bookmark Button (optional) */}
        {showBookmark && (
          <TouchableOpacity
            style={[
              styles.bookmarkButton,
              { width: bookmarkSize, height: bookmarkSize },
              isBookmarked && styles.bookmarkButtonSaved,
              isLargeScreen && styles.bookmarkButtonLarge,
            ]}
            onPress={onBookmarkPress}
            activeOpacity={0.8}
          >
            {isBookmarked ? (
              <BookmarkFilledIcon
                width={bookmarkIconSize}
                height={bookmarkIconSize}
                color={MARKETPLACE_COLORS.textPrimary}
              />
            ) : (
              <BookmarkIcon
                width={bookmarkIconSize}
                height={bookmarkIconSize}
                color={MARKETPLACE_COLORS.textPrimary}
              />
            )}
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <View style={[styles.carouselWrapper, shouldShowArrows && styles.carouselWrapperWithArrows]}>
      {/* Left Arrow */}
      {shouldShowArrows && (
        <TouchableOpacity
          style={[styles.arrowButton, styles.arrowButtonLeft, !canGoPrev && styles.arrowButtonDisabled]}
          onPress={goToPrev}
          disabled={!canGoPrev}
          activeOpacity={0.7}
        >
          <ChevronLeftIcon 
            width={24} 
            height={24} 
            color={canGoPrev ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted} 
          />
        </TouchableOpacity>
      )}

      <View style={[styles.container, { width: imageWidth, height: imageHeight }]}>
        <FlatList
          ref={flatListRef}
          data={images}
          renderItem={renderItem}
          keyExtractor={(_, index) => index.toString()}
          horizontal
          pagingEnabled
          showsHorizontalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
          getItemLayout={(_, index) => ({
            length: imageWidth,
            offset: imageWidth * index,
            index,
          })}
        />

        {/* Bookmark Button (optional) */}
        {showBookmark && (
          <TouchableOpacity
            style={[
              styles.bookmarkButton,
              { width: bookmarkSize, height: bookmarkSize },
              isBookmarked && styles.bookmarkButtonSaved,
              isLargeScreen && styles.bookmarkButtonLarge,
            ]}
            onPress={onBookmarkPress}
            activeOpacity={0.8}
          >
            {isBookmarked ? (
              <BookmarkFilledIcon
                width={bookmarkIconSize}
                height={bookmarkIconSize}
                color={MARKETPLACE_COLORS.textPrimary}
              />
            ) : (
              <BookmarkIcon
                width={bookmarkIconSize}
                height={bookmarkIconSize}
                color={MARKETPLACE_COLORS.textPrimary}
              />
            )}
          </TouchableOpacity>
        )}

        {/* Carousel Dots */}
        {images.length > 1 && (
          <View style={[
            styles.dotsContainer,
            dotsPosition === 'bottom-left' && styles.dotsContainerLeft,
          ]}>
            <CarouselDots
              count={images.length}
              activeIndex={activeIndex}
              containerStyle={styles.dotsContainerOverride}
            />
          </View>
        )}
      </View>

      {/* Right Arrow */}
      {shouldShowArrows && (
        <TouchableOpacity
          style={[styles.arrowButton, styles.arrowButtonRight, !canGoNext && styles.arrowButtonDisabled]}
          onPress={goToNext}
          disabled={!canGoNext}
          activeOpacity={0.7}
        >
          <ChevronRightIcon 
            width={24} 
            height={24} 
            color={canGoNext ? MARKETPLACE_COLORS.textPrimary : MARKETPLACE_COLORS.textMuted} 
          />
        </TouchableOpacity>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  carouselWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  carouselWrapperWithArrows: {
    gap: 16,
  },
  container: {
    borderRadius: 16,
    overflow: 'hidden',
    backgroundColor: MARKETPLACE_COLORS.cardBackground,
    position: 'relative',
  },
  placeholderContainer: {
    backgroundColor: MARKETPLACE_COLORS.uploadAreaBackground,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Arrow navigation buttons
  arrowButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: MARKETPLACE_COLORS.white10Percent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  arrowButtonLeft: {
    // Left arrow positioning
  },
  arrowButtonRight: {
    // Right arrow positioning
  },
  arrowButtonDisabled: {
    opacity: 0.4,
  },
  imageSlide: {
    borderRadius: 16,
  },
  carouselImage: {
    borderRadius: 16,
  },
  bookmarkButton: {
    position: 'absolute',
    top: 20,
    right: 20,
    width: 34,
    height: 34,
    borderRadius: 8,
    backgroundColor: MARKETPLACE_COLORS.priceBadgeBg,
    alignItems: 'center',
    justifyContent: 'center',
  },
  bookmarkButtonLarge: {
    top: 24,
    right: 24,
    borderRadius: 10,
  },
  bookmarkButtonSaved: {
    backgroundColor: MARKETPLACE_COLORS.accentRed,
  },
  dotsContainer: {
    position: 'absolute',
    bottom: 20,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  dotsContainerLeft: {
    alignItems: 'flex-start',
    left: 20,
    right: 'auto',
  },
  dotsContainerOverride: {
    position: 'relative',
    bottom: 0,
  },
});

